import React, {useState} from 'react'
import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography} from "@mui/material";
import VerifyBankAccountForm from "./VerifyBankAccountForm";
import {parseCurrencyValue, validateCurrencyValue} from "../../util/PaymentUtils";
import {requestPartnerVerifyBankAccount} from "./request/payment-method-requests";
import {getErrorMessageForStandardResponse} from "../../util/NetworkErrorUtil";
import Busy from "../Busy";
import {styled} from "@mui/material/styles";

const PartnerVerifyBankAccountDialogActions = styled(DialogActions)(({theme}) => ({
  backgroundColor: theme.palette.grey[100]
}));

interface VerifyPaymentMethodFields {
  // Deposit amounts are integers (cents)
  microDeposit1: number;
  microDeposit2: number;
}

/** Dwolla validation error body, JSON-encoded in the standard error response message */
interface DwollaValidationErrorBody {
  _embedded: {
    errors: { path?: string }[];
  };
}

interface PartnerVerifyBankAccountDialogProps {
  fundingSourceId?: string;
  accountId?: string;
  open?: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PartnerVerifyBankAccountDialog = ({
                                          fundingSourceId = '',
                                          accountId = '',
                                          open = false,
                                          onClose,
                                          onSuccess
                                        }: PartnerVerifyBankAccountDialogProps) => {
  const [verifyPaymentMethodFields, setVerifyPaymentMethodFields] = useState<VerifyPaymentMethodFields>({
    microDeposit1: 0,
    microDeposit2: 0,
  });
  const [errorMsg, setErrorMsg] = useState('');

  const resetForm = (): void => {
    setVerifyPaymentMethodFields({
      ...verifyPaymentMethodFields,
      microDeposit1: 0,
      microDeposit2: 0,
    });
    setErrorMsg('');
  };

  const handleClose = (): void => {
    resetForm();
    onClose();
  }

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const {name, value} = event.target;
    switch (name) {
      case 'firstDepositAmount':
        if (!validateCurrencyValue(value)) return;
        setVerifyPaymentMethodFields({
          ...verifyPaymentMethodFields,
          microDeposit1: parseCurrencyValue(value || 0)
        });
        break;
      case 'secondDepositAmount':
        if (!validateCurrencyValue(value)) return;
        setVerifyPaymentMethodFields({
          ...verifyPaymentMethodFields,
          microDeposit2: parseCurrencyValue(value || 0)
        });
        break;
      default:
        break;
    }
  };

  // Used as both the form's onSubmit and the submit button's onClick
  const handleSubmit = (event: React.SyntheticEvent): void => {
    event.preventDefault();
    if (!fundingSourceId || !accountId) return;
    const body = {
      fundingSourceId: fundingSourceId,
      accountId: accountId,
      ...verifyPaymentMethodFields
    };
    Busy.set(true);
    void requestPartnerVerifyBankAccount(body).then(() => {
      resetForm();
      onSuccess();
    }).catch((error: unknown) => {
      // Any parse failure or unexpected shape falls through to the generic message in the catch below
      try {
        const rawErrorMsg = getErrorMessageForStandardResponse(
          error,
          "Failed to verify bank account. Please try again."
        );
        const errorObj = JSON.parse(rawErrorMsg) as DwollaValidationErrorBody;
        let message = 'Failed to verify bank account.';
        const embeddedErrors = errorObj._embedded.errors;
        if (embeddedErrors[0].path === '/amount1/value') {
          message += "Invalid amount for first deposit.";
        }
        if (embeddedErrors[1].path === '/amount2/value') {
          message += "Invalid amount for second deposit.";
        }
        setErrorMsg(message);
      } catch {
        setErrorMsg("Failed to verify bank account. Please try again.");
      }
    }).finally(() => {
      Busy.set(false);
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby={'ss-bank-verification-title'}
      aria-describedby={'ss-bank-verification-steps-and-form'}
    >
      <DialogTitle id={'ss-bank-verification-title'}>
        <Typography variant={'h6'} component={'h2'}>Add Payment Method</Typography>
      </DialogTitle>
      <DialogContent id={'ss-bank-verification-steps-and-form'}>
        <VerifyBankAccountForm
          formId={'ss-verify-bank-form'}
          firstDepositAmountValue={verifyPaymentMethodFields.microDeposit1}
          secondDepositAmountValue={verifyPaymentMethodFields.microDeposit2}
          onSubmit={handleSubmit}
          onChange={handleTextChange}
        />
        {
          errorMsg &&
          <Typography variant={'body1'} color={'error'} align={'center'}>{errorMsg}</Typography>
        }
      </DialogContent>
      <PartnerVerifyBankAccountDialogActions>
        <Button color={'primary'} onClick={handleClose}>
          Cancel
        </Button>
        <Button color={'primary'} type={'submit'} onClick={handleSubmit} form={'ss-verify-bank-form'}>
          Verify Bank Account
        </Button>
      </PartnerVerifyBankAccountDialogActions>
    </Dialog>
  );
};

export default PartnerVerifyBankAccountDialog;
